import { camelCase } from 'change-case'
import { decodeHtmlEntities, getNestedProperty } from '@opallabs/lambda'
import {
  setAssetReferenceData,
  setCarouselAssetData,
  setReactModularSectionOptionData,
  setComponentData,
} from '../dataSetters'

export const mapPostOptions = (
  post_options,
  optionValues = [],
  coverAssets = []
) =>
  post_options.reduce((accum, postOption) => {
    if (!postOption) return accum
    const optionMap = { ...accum }
    const option = postOption.option_record && postOption.option_record.option

    if (!option) return accum
    const optionName = camelCase(option.system_name)
    // there are three keys a postOption's value could be stored in
    // value as string or boolean
    // option_value object as display_name
    // asset_reference object
    // if a postOption has an asset_reference that asset's uuid
    // is stored in the value key so we'll want to prefer the AR data
    let value = decodeHtmlEntities(postOption.value)

    if (postOption.asset_reference) {
      value = setAssetReferenceData(postOption.asset_reference)
      const coverAsset = coverAssets?.find(
        (asset) =>
          asset?.id === postOption.attributes?.cover_asset_reference?.id ||
          asset?.id === postOption.asset_reference?.cover_asset_reference_id
      )
      if (coverAsset)
        value.cover_asset_reference = setAssetReferenceData(coverAsset)
    }

    // OptionValue model is not serialized in the post's toJSON method
    // instead we look up a selected option value by id if any are present
    // using an array of option values that is always passed down when updating post options
    if (postOption.option_value_id && optionValues.length) {
      const optionValue = optionValues.find(
        (optValue) => optValue.id === postOption.option_value_id
      )
      value = optionValue.display_name
    }

    optionMap[optionName] = value

    return optionMap
  }, {})

export const mapPostOptionsForCarouselComponent = (
  postOptions,
  optionValues,
  coverAssets = []
) => {
  // we'll pull all assets from postOptions into a single array
  const carouselAssets = []
  const assetPostOptions = postOptions.filter(
    (postOption) => postOption.asset_reference !== null
  )
  // postOptions with parentPostOptions are child options that hold extra data related to the parent postOption
  // in the case of carousel assets it's information about a URL that asset would link to
  // carousel assets are the only postOptions that have child postOptions at this time
  const childPostOptions = postOptions.filter(
    (postOption) =>
      postOption.parent_post_option !== null &&
      postOption.parent_post_option !== undefined
  )
  // these would be any postOptions that appear in the cog menu to the right of the post in the composer
  const nonAssetPostOptions = postOptions.filter(
    (postOption) =>
      !(postOption.asset_reference || postOption.parent_post_option)
  )
  // create a simple key value pair from these other postOptions like we would if
  // they were not part of post components
  const otherOptionAttributes = mapPostOptions(
    nonAssetPostOptions,
    optionValues,
    coverAssets
  )
  // we'll start with child postOptions because their parent_post_option key
  // contains all the data of the parent postOption so we don't have to match them later
  if (childPostOptions.length) {
    childPostOptions.forEach((postOption) => {
      // we can assume here that all parent postOptions are assets
      const parentPostOption = postOption.parent_post_option
      const carouselAsset = setCarouselAssetData({
        postOption,
        parentPostOption,
      })
      // remove the parent postOptions from our array of asset postOptions
      // we have all the data we need from it
      const parentPostOptionIndex = assetPostOptions.indexOf(parentPostOption)
      assetPostOptions.splice(parentPostOptionIndex, 1)
      carouselAssets.push(carouselAsset)
    })
  }

  // assetPostOptions should now no longer have postOptions that have child postOptions
  if (assetPostOptions.length) {
    assetPostOptions.forEach((postOption) => {
      const carouselAsset = setCarouselAssetData({ postOption })
      carouselAssets.push(carouselAsset)
    })
  }

  return {
    ...otherOptionAttributes,
    assets: carouselAssets,
  }
}

export const mapReactModularSectionOptions = (post_options) =>
  // Similar to mapPostOptions functionality except it passes post_options through the
  // setCarouselOptionData data setter for the asset carousel component
  post_options.reduce((accum, postOption) => {
    if (!postOption) return accum
    const optionMap = { ...accum }
    const option = postOption.option_record && postOption.option_record.option
    const optionName = camelCase(option.system_name)
    const value = setReactModularSectionOptionData(postOption)

    optionMap[optionName] = value

    return optionMap
  }, {})

export const constructModuleData = (
  postComponents,
  componentPostOptions,
  optionValues,
  coverAssets = []
) =>
  postComponents.map((postComponent) => {
    // attributes that are stored on the postComponent and not in postOptions
    const componentAttributes = setComponentData(postComponent)
    // postOptions that store data for this postComponent
    let matchingPostOptions = componentPostOptions?.filter(
      (postOption) =>
        (postComponent.guid &&
          postOption.post_component_guid === postComponent.guid) ||
        (postComponent.componentId &&
          postOption.component_position === postComponent.position &&
          postOption.component_id === postComponent.componentId) ||
        (postComponent.id && postOption.post_component_id === postComponent.id)
    )
    // grab post options meant for the asset carousel section options popover, signified by 'is_section_option' metadata flag
    const reactModularSectionOptions = matchingPostOptions?.filter(
      (postOption) => {
        const metaData = getNestedProperty(
          postOption,
          'option_record',
          'option',
          'meta_data'
        )
        return metaData && metaData.is_section_option
      }
    )

    // not the ideal way to check component type option but works for now
    let component = getNestedProperty(postComponent, 'component', 'system_name')
    if (!component) component = postComponent.componentName
    const isCarouselComponent = component && component.includes('carousel')

    if (reactModularSectionOptions.length) {
      return {
        ...componentAttributes,
        ...mapPostOptions(matchingPostOptions, optionValues, coverAssets),
        ...mapReactModularSectionOptions(
          reactModularSectionOptions,
          optionValues
        ),
      }
    }

    if (isCarouselComponent) {
      return {
        ...componentAttributes,
        ...mapPostOptionsForCarouselComponent(
          matchingPostOptions,
          optionValues,
          coverAssets
        ),
      }
    }

    return {
      ...componentAttributes,
      ...mapPostOptions(matchingPostOptions, optionValues),
    }
  })

export const setPostOptionAndComponentData = (
  post,
  postComponents,
  optionValues,
  coverAssets = []
) => {
  // postOptions not attached to a postComponent
  const globalPostOptions = post.post_options.filter(
    (postOption) => postOption.post_component_id === null
  )
  // postOptions attached to a postComponent, sort postcomponent by position attribute
  const componentPostOptions = post.post_options.filter(
    (postOption) =>
      postOption.post_component_guid !== null ||
      postOption.post_component_id !== null
  )
  postComponents.sort((a, b) => a.position - b.position)

  // attributes for postOptions that are not attached to postComponents
  const postOptionData = mapPostOptions(globalPostOptions, optionValues)
  // a postComponent and its associated postOptions will be grouped into one object
  // as part of a larger modules array
  let moduleData = null

  if (postComponents && postComponents.length) {
    moduleData = constructModuleData(
      postComponents,
      componentPostOptions,
      optionValues,
      coverAssets
    )
  }

  return { ...postOptionData, modules: moduleData }
}
